// src/components/dentally/MappingShell.jsx
//
// One shell for the Dentally mapping tools (treatment / practitioner sync).
// `inline={false}` renders the existing MUI Dialog (Integration settings);
// `inline` renders the same title / content / actions inside a Card so the
// Dentally workspace can host the identical mapping implementation without a
// second copy of the mapping logic. Children use the slot components below.
import { createContext, useContext } from 'react';
import PropTypes from 'prop-types';
import {
  Box, Card, Dialog, DialogActions, DialogContent, DialogTitle, Divider,
} from '@mui/material';

const ShellContext = createContext({ inline: false });
const useShell = () => useContext(ShellContext);

export default function MappingShell({ inline, open, onClose, maxWidth, children }) {
  if (inline) {
    return (
      <ShellContext.Provider value={{ inline: true }}>
        <Card data-testid="mapping-shell-inline" sx={{ display: 'flex', flexDirection: 'column' }}>
          {children}
        </Card>
      </ShellContext.Provider>
    );
  }
  return (
    <ShellContext.Provider value={{ inline: false }}>
      <Dialog open={open} onClose={onClose} maxWidth={maxWidth} fullWidth>
        {children}
      </Dialog>
    </ShellContext.Provider>
  );
}

MappingShell.propTypes = {
  inline: PropTypes.bool,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  maxWidth: PropTypes.string,
  children: PropTypes.node,
};
MappingShell.defaultProps = { inline: false, open: false, onClose: undefined, maxWidth: 'md', children: null };

export function MappingShellTitle({ children, sx }) {
  const { inline } = useShell();
  if (inline) {
    return <Box sx={{ px: 3, py: 2, ...sx }}>{children}</Box>;
  }
  return <DialogTitle sx={sx}>{children}</DialogTitle>;
}
MappingShellTitle.propTypes = { children: PropTypes.node, sx: PropTypes.object };
MappingShellTitle.defaultProps = { children: null, sx: undefined };

export function MappingShellContent({ children, dividers }) {
  const { inline } = useShell();
  if (inline) {
    return (
      <>
        {dividers && <Divider />}
        <Box sx={{ px: 3, py: 2 }}>{children}</Box>
        {dividers && <Divider />}
      </>
    );
  }
  return <DialogContent dividers={dividers}>{children}</DialogContent>;
}
MappingShellContent.propTypes = { children: PropTypes.node, dividers: PropTypes.bool };
MappingShellContent.defaultProps = { children: null, dividers: false };

export function MappingShellActions({ children, sx }) {
  const { inline } = useShell();
  if (inline) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, px: 3, py: 1.5, ...sx }}>
        {children}
      </Box>
    );
  }
  return <DialogActions sx={sx}>{children}</DialogActions>;
}
MappingShellActions.propTypes = { children: PropTypes.node, sx: PropTypes.object };
MappingShellActions.defaultProps = { children: null, sx: undefined };

/** True when rendered inside an inline shell (hosts hide dialog-only Close controls). */
export const useMappingShellInline = () => useShell().inline;
